import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { useLang } from '../../context/LangContext';

/* ── Types ──────────────────────────────────────────── */

export interface AlgorithmVariable {
  name: string;
  question_orders: number[];
}

export interface AlgorithmData {
  variables: AlgorithmVariable[];
  code: string;
}

/* ── Parse / Serialize ──────────────────────────────── */

function parse(str: string): AlgorithmData {
  if (!str || !str.trim()) return { variables: [], code: '' };
  try {
    const p = JSON.parse(str);
    if (p && typeof p === 'object' && Array.isArray(p.variables)) {
      return {
        variables: p.variables.map((v: Record<string, unknown>) => ({
          name: (v.name as string) || '',
          question_orders: Array.isArray(v.question_orders)
            ? (v.question_orders as unknown[])
                .map(Number)
                .filter((n) => !isNaN(n as number) && (n as number) > 0)
            : [],
        })),
        code: typeof p.code === 'string' ? p.code : '',
      };
    }
  } catch {
    // ignored
  }
  return { variables: [], code: '' };
}

function serialize(algo: AlgorithmData): string {
  if (algo.variables.length === 0 && !algo.code.trim()) return '';
  return JSON.stringify(algo);
}

function parseOrders(str: string): number[] {
  return str
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n) && n > 0);
}

/* ── Styled ─────────────────────────────────────────── */

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.background};
`;

const SectionTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

const SmallLabel = styled.span`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textLight};
  white-space: nowrap;
`;

const VarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 6px;
`;

const VarNameInput = styled(Input)`
  width: 150px;
  min-width: 80px;
`;

const VarOrdersInput = styled(Input)`
  width: 180px;
  min-width: 80px;
`;

const CodeArea = styled.textarea`
  width: 100%;
  min-height: 160px;
  font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
  font-size: 0.88rem;
  line-height: 1.6;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  resize: vertical;
  box-sizing: border-box;
  outline: none;
  spellcheck: false;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textLight};
    opacity: 0.6;
  }
`;

const RefBox = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 8px 12px;
  margin-top: 8px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.8;
`;

const RefCode = styled.code`
  background: ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  padding: 1px 5px;
  font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.text};
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: 12px 0;
`;

const Hint = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 8px;
`;

/* ── Component ──────────────────────────────────────── */

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const AlgorithmEditor: React.FC<Props> = ({ value, onChange, disabled }) => {
  const { t } = useLang();
  const algo = useMemo(() => parse(value), [value]);

  // Raw strings for order inputs
  const [orderTexts, setOrderTexts] = useState<string[]>(() =>
    algo.variables.map((v) => v.question_orders.join(', ')),
  );
  const prevVarCount = useRef(algo.variables.length);
  useEffect(() => {
    if (algo.variables.length !== prevVarCount.current) {
      prevVarCount.current = algo.variables.length;
      setOrderTexts(algo.variables.map((v) => v.question_orders.join(', ')));
    }
  }, [algo.variables]);

  const update = useCallback(
    (next: AlgorithmData) => onChange(serialize(next)),
    [onChange],
  );

  const addVariable = () => {
    update({ ...algo, variables: [...algo.variables, { name: '', question_orders: [] }] });
  };

  const removeVariable = (idx: number) => {
    update({ ...algo, variables: algo.variables.filter((_, i) => i !== idx) });
  };

  const updateVarName = (idx: number, name: string) => {
    update({ ...algo, variables: algo.variables.map((v, i) => (i === idx ? { ...v, name } : v)) });
  };

  const updateVarOrders = (idx: number, orders: number[]) => {
    update({ ...algo, variables: algo.variables.map((v, i) => (i === idx ? { ...v, question_orders: orders } : v)) });
  };

  const updateCode = (code: string) => {
    update({ ...algo, code });
  };

  // Enter внутри полей переменных не должен отправлять внешнюю <form> редактора теста
  // (иначе происходит неявный submit и переход со страницы /test-editor/:id на /tests).
  const preventFormSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const varNames = algo.variables.map((v) => v.name).filter(Boolean);

  const placeholder = [
    '// Условия проверяются по порядку',
    'если сумма(Переменная1) >= 5 тогда взять(A)',
    'если сумма(Переменная1) < 5 тогда взять(B)',
    'если сумма(Переменная2) > сумма(Переменная1) тогда взять(C)',
    'иначе взять(A)',
    'взять(A)  // все вопросы вне переменных',
  ].join('\n');

  return (
    <Container>
      {/* ── Variables ── */}
      <SectionTitle>{t('algoVarsTitle')}</SectionTitle>

      {algo.variables.length === 0 && (
        <div style={{ fontSize: '0.82rem', color: '#888', marginBottom: 8 }}>
          {t('algoNoVariables')}
        </div>
      )}

      {algo.variables.map((v, idx) => (
        <VarRow key={idx}>
          <VarNameInput
            value={v.name}
            onChange={(e) => updateVarName(idx, e.target.value)}
            onKeyDown={preventFormSubmit}
            placeholder={t('algoVarName')}
            disabled={disabled}
          />
          <SmallLabel>{t('algoVarQuestions')}:</SmallLabel>
          <VarOrdersInput
            value={orderTexts[idx] ?? v.question_orders.join(', ')}
            onChange={(e) => {
              const raw = e.target.value;
              setOrderTexts((prev) => prev.map((s, i) => (i === idx ? raw : s)));
              updateVarOrders(idx, parseOrders(raw));
            }}
            onKeyDown={preventFormSubmit}
            placeholder={t('algoVarQuestionsHint')}
            disabled={disabled}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => removeVariable(idx)}
            disabled={disabled}
          >
            {t('algoVarDelete')}
          </Button>
        </VarRow>
      ))}

      <Button variant="text" size="sm" onClick={addVariable} disabled={disabled} style={{ marginBottom: 4 }}>
        {t('algoAddVariable')}
      </Button>

      <Divider />

      {/* ── Code editor ── */}
      <SectionTitle>{t('algoCodeTitle')}</SectionTitle>

      <CodeArea
        value={algo.code}
        onChange={(e) => updateCode(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        spellCheck={false}
      />

      {/* ── Reference ── */}
      <RefBox>
        <div>
          <b>{t('algoRefFunctions')}:</b>{' '}
          <RefCode>сумма(X)</RefCode> <RefCode>среднее(X)</RefCode>{' '}
          <RefCode>макс(X)</RefCode> <RefCode>мин(X)</RefCode>
        </div>
        <div>
          <b>{t('algoRefOps')}:</b>{' '}
          <RefCode>&gt;</RefCode> <RefCode>&gt;=</RefCode> <RefCode>&lt;</RefCode>{' '}
          <RefCode>&lt;=</RefCode> <RefCode>==</RefCode> <RefCode>!=</RefCode>{' '}
          <RefCode>и</RefCode> <RefCode>или</RefCode>
        </div>
        <div>
          <b>{t('algoRefActions')}:</b>{' '}
          <RefCode>взять(Буква)</RefCode>
          <span style={{ marginLeft: 6, opacity: 0.7 }}>— {t('algoRefTakeHint')}</span>
        </div>
        {varNames.length > 0 && (
          <div>
            <b>{t('algoRefVars')}:</b>{' '}
            {varNames.map((n, i) => <RefCode key={i}>{n}</RefCode>).reduce<React.ReactNode[]>(
              (acc, el, i) => (i === 0 ? [el] : [...acc, ' ', el]),
              [],
            )}
          </div>
        )}
      </RefBox>

      <Hint>{t('algoHint')}</Hint>
    </Container>
  );
};
